import { useAuth } from '@skyslope/auth-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import useCustomSnackbar from 'hooks/useCustomSnackbar';
import type { IntegrationEnablement, IntegrationKey } from 'resources/server/IntegrationsServer';
import integrationsServer from 'resources/server/IntegrationsServer';

import { getIntegrationsQueryKey } from './useGetIntegrations';

interface useSetIntegrationEnablementOptions {
  callSnackbar?: boolean;
}

export const useSetIntegrationEnablement = (options?: useSetIntegrationEnablementOptions) => {
  const { userContext } = useAuth();
  const { addSuccessSnackbar, addErrorSnackbar, addInfoSnackbar } = useCustomSnackbar();
  const queryClient = useQueryClient();

  const callSnackbar = options?.callSnackbar === undefined || options?.callSnackbar;
  return useMutation({
    mutationFn: async ({
      integrationType,
      switchToEnablement,
    }: {
      integrationType: IntegrationKey;
      switchToEnablement: Omit<IntegrationEnablement['userEnablement'], 'pending'>;
    }) => {
      if (!userContext?.id) {
        throw new Error('User ID is required');
      }

      const userIntegrations = await integrationsServer.GetUserIntegrations(userContext.id);
      const userCurrentEnablement = userIntegrations[integrationType]?.userEnablement;
      if (userCurrentEnablement === 'pending' || userCurrentEnablement === switchToEnablement) {
        return {
          ...userIntegrations[integrationType],
          skippedToggle: true,
          switchToEnablement,
        };
      }
      return {
        ...(await integrationsServer.ToggleIntegration(integrationType, userContext.id)),
        skippedToggle: false,
        switchToEnablement,
      };
    },
    onSuccess: (data) => {
      if (callSnackbar) {
        if (data.skippedToggle) {
          if (data.userEnablement === 'pending') {
            addErrorSnackbar('Integration is pending', {
              autoHideDuration: 5000,
            });
          } else {
            addInfoSnackbar(`Integration already is ${data.switchToEnablement}`, {
              autoHideDuration: 5000,
            });
          }
        } else {
          addSuccessSnackbar('Activation successful', {
            autoHideDuration: 5000,
          });
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [getIntegrationsQueryKey] });
    },
    onError: () => {
      if (callSnackbar) {
        addErrorSnackbar('Activation failed');
      }
    },
  });
};
